import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { Grid } from "@mui/material";
import OperatorMSModal from "./OperatorMSModal";
import {
  CommonTypeTabsGroup,
  CommonRangeSelectSlider,
  CommonToggleWrapper,
  CommonRadioSelectGroup,
  DisabledInfoMessage,
} from "../../common";
import SitesDetailsTile from "../../SitesDetailTile";
import styled from "styled-components";
import { HorizontalSeparator } from "../../../styled/SidePanel.style";
import { OP_MS_DIVISIONAL_TABLE_COLUMN, OP_MS_GEOHASH_TABLE_COLUMN, OP_MS_RADIO_DATA, OP_MS_TRANSMISSION_DATA } from "../../../../constants/Data";
import { getAllDistinctWithCount, getFilters } from "../../../../services/api/filterService";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import OperatorMSMapElements from "./OperatorMSMapElements";
import { DATA_ORDERS } from "../../../../constants/WebMap";
import { renameLabels, sortByReference } from "../../../../helpers/fuctionHelpers";
import CommonHorizMultiSelect from "../../common/CommonHorizMultiSelect";
import { shouldAddToQuery } from "../../../../helpers/webMap";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import { fetchMinMaxMSOp, getQuadCounts } from "../../../../services/api/marketShare.api";
import { adminLevelToAdm } from "../../../../constants/WebMapApi";
import WarningModal from "./WarningModal";

const StyledContainer = styled.div`
  padding-top: 5px;
  margin: 10px 5px;
  width: 100%;
`;

const StyledGridContainer = styled(Grid)`
  && {
    justify-content: space-between;
    margin: 15px 5px 0px;
    padding: 10px;
    width: 100%;
    font: normal normal 550 13px/18px Open Sans;
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 1px solid #007842;
    border-radius: 5px;
    opacity: 1;
    margin-bottom: 10px;
  }
`;

const StyledGridTitle = styled(Grid)`
  margin-bottom: 10px !important;
  text-align: left;
  font: normal normal 550 13px/18px Open Sans;
  color: black;
`;

const OperatorMSPanel = ({
  isVisible,
  eyeIcon,
  topMSChecked,
  isModalOpen,
  opMSAggrPerm,
  handleModalClose
}) => {
  // status values
  const { appliedFilters, msConfig, msConfigType, setMsConfig, globalConfig, setMsGeohashOnMap, operatorsPermittedMS = [] } = useContext(WebMapContext);
  const { countryCode, year, quarter, level, levelVal } = appliedFilters;
  const skipLevelFilters = [OP_MS_RADIO_DATA[5].value, OP_MS_RADIO_DATA[6].value, OP_MS_RADIO_DATA[7].value, OP_MS_RADIO_DATA[8].value];

  // tables related consts
  const adminLevels = [
    { label: globalConfig?.adminLevelLabels?.adm1 || "", field: "adm1",   minWidth: 70 , },
    { label: globalConfig?.adminLevelLabels?.adm2 || "", field: "adm2",   minWidth: 70 , },
    { label: globalConfig?.adminLevelLabels?.adm3 || "", field: "adm3",   minWidth: 70 , },
  ];
  const geoHashTableColumns = adminLevels.concat(OP_MS_GEOHASH_TABLE_COLUMN);
  const devisionalTableColumns = adminLevels.concat(
    OP_MS_DIVISIONAL_TABLE_COLUMN
  );

  function filterColumnsByLevel(columns, level = "ADM1") {
    if (level === "ADM1") {
      return columns.filter(
        (column) => column.field !== "adm2" && column.field !== "adm3"
      );
    } else if (level === "ADM2") {
      return columns.filter((column) => column.field !== "adm3");
    } else {
      return columns;
    }
  }

  const [selectedOperatorMS, setSelectedOperatorMS] = useState('');
  const [range, setRange] = useState([0, 100]);
  const [borderLvls, setBorderLvls] = useState([]);
  const [allBorderLvls, setAllBorderLvls] = useState([]); // all levels without disabling based on geo adminLevel
  const [borderLvlValue, setBorderLvlValue] = useState();
  const [connectionValue, setConnectionValue] = useState();
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [aggValue, setAggValue] = useState();
  const [aggTypes, setAggTypes] = useState([]);
  const [panelFilters, setPanelFilters] = useState();
  const [radioValue, setRadioValue] = useState(OP_MS_RADIO_DATA[0]?.value);
  const [conditionFilters, setConditionFilters] = useState([]);
  const [quadFilters, setQuadFilters] = useState([]);
  const [quadCount, setQuadCount] = useState();
  const [actionFilters, setActionFilters] = useState([]);
  const [msRanks, setMsRanks] = useState([]);
  const [selectedMS, setSelectedMS] = useState([]); // selected table rows
  const [selectedGeohashMS, setSelectedGeohashMS] = useState([]);
  const [filtersTrigger, setFiltersTrigger] = useState(0); // state to indicate filters got changed
  const [mapPriority, setMapPriority] = useState("divTable"); // map visualization priority: divTable or geoTable data
  const [resetPopup, setResetPopup] = useState({ open: false, cb: () => { } }); // modal data reset popup
  const hasDisabledTab = borderLvls.some(lvl => lvl.disabled);
  const [operatorData, setOperatorData] = useState([]);
  const [minMaxMSOp, setMinMaxMSOp] = useState([]);

  const filteredColumns = filterColumnsByLevel(
    devisionalTableColumns,
    borderLvlValue
  );
  const [geohashfilteredColumns, setGeohashfilteredColumns] = useState(filterColumnsByLevel(
    geoHashTableColumns,
    borderLvlValue
  ))
  const [msSort, setMsSort] = useState({ sortBy: filteredColumns[0].field, sortOrder: "asc" })
  const [geoMsSort, setGeoMsSort] = useState({ sortBy: geohashfilteredColumns[0].field, sortOrder: "asc" })
  const [refresh, setRefresh] = useState(false);

  //useEffect ======================================================
  useEffect(() => {
    if (countryCode && year && quarter) {
      setBorderLvlValue();
      setBorderLvls([]);
      setRange([0, 100]);
    }
  }, [countryCode, year, quarter]);

  useEffect(() => {
      setRefresh(prev => !prev); // This forces a re-render
      setSelectedOperatorMS(operatorsPermittedMS[0]?.operator);
  }, [year, quarter]);

  useEffect(() => {
    const init = async () => {
      if(isVisible){
        setOperatorData(operatorsPermittedMS);
        if(!selectedOperatorMS){
          setSelectedOperatorMS(operatorsPermittedMS[0]?.operator)
        }
      }
    };
    init();
  }, [isVisible, operatorsPermittedMS]);

  useEffect(() => {
    // fetch initial ADM1,2,3. After this Excellent,Good,fair will also fetched automatically
    if (!topMSChecked) {
      fetchRadioFilters("operator_market_share", "lvl", {},
        (data) => {
          const lvlArr = sortByReference(data, DATA_ORDERS.ADM, "label");
          const modifiedLvls = lvlArr.map(obj => {
            return {
              ...obj,
              label: globalConfig && globalConfig?.adminLevelLabels ? globalConfig?.adminLevelLabels[(obj.label).toLowerCase()] : obj.label
            };
          });
          setAllBorderLvls(modifiedLvls);
          const newlvl = modifiedLvls.some(x => x.value === borderLvlValue) ? borderLvlValue : modifiedLvls[0]?.value;
          if (level && levelVal) { // fetch after state update setteled down
            adjustLvlBasedOnAdmLevel(newlvl, modifiedLvls);
          } else {
            setBorderLvls(modifiedLvls);
            setBorderLvlValue(newlvl);
            fetchCTFilters({ lvl: newlvl });
          }
        });
    } else {
      setMsGeohashOnMap(false);
    }
  }, [topMSChecked, level, levelVal, refresh]);

  useEffect(() => {
    // fetch ranks based on geo adm lvl changes
    if ((isVisible || eyeIcon) && !topMSChecked && connectionTypes.length > 0) {
      fetchRankFilters({ });
    }
  }, [ level, levelVal]);

  useEffect(() => {
    setGeohashfilteredColumns(filterColumnsByLevel(geoHashTableColumns, borderLvlValue))
  }, [level, borderLvlValue]);

  useEffect(() => {
    // reset sorting
    setMsSort({ sortBy: filteredColumns[0].field, sortOrder: "asc" })
    setGeoMsSort({ sortBy: geohashfilteredColumns[0].field, sortOrder: "asc" })

    setSelectedMS([]); // reset selected rows
    setSelectedGeohashMS([]);
    if (selectedGeohashMS.length) setMsGeohashOnMap(true); // currently viewing geohashes
  }, [
    countryCode,
    year,
    quarter,
    level, levelVal,
    borderLvlValue, connectionValue,
    selectedOperatorMS,
    range, msRanks,
    actionFilters,
    quadFilters
  ]);

  useEffect(() => {
    // set ms level & its filters when visualizing geohashes
    if (mapPriority === "geoTable") {
      const isDisabledOptions = conditionFilters.find(x => x.disabled);
      if (isDisabledOptions) { // enable all options for geohashes
        const filters = conditionFilters.map(x => ({ ...x, isChecked: true, disabled: false }));
        setConditionFilters(filters);
      }
      const invalidGeohashLvls = OP_MS_RADIO_DATA.slice(-2).map(x => x.value); // reset radio value if it is in a lvl that not applies to geohash
      if (invalidGeohashLvls.includes(radioValue)) setRadioValue(OP_MS_RADIO_DATA[6]?.value);
    }
  }, [mapPriority, conditionFilters]);

  useEffect(() => {
    // set ms level filters when returning back geohash visualization
    if (mapPriority === "divTable" && !isModalOpen && conditionFilters.length) {
      fetchMsLvlFilters({ radioVal: radioValue });
    }
  }, [mapPriority]);

  useEffect(() => {
    if(OP_MS_TRANSMISSION_DATA.some(item => item.value === radioValue)){
      getMinMaxMSOp(radioValue)
    }
  }, [borderLvlValue]);

  // show data reset warning popup when visualized from MS modal
  const isResetModal = useMemo(() => {
    return eyeIcon && !topMSChecked && !isModalOpen && (selectedMS.length > 0 ||
      (mapPriority === "geoTable" && selectedGeohashMS.length > 0));
  }, [eyeIcon, topMSChecked, isModalOpen, selectedMS, selectedGeohashMS]);

  const MS_LEVELS = useMemo(() => {
    return mapPriority === "geoTable"
      ? OP_MS_RADIO_DATA.slice(0, OP_MS_RADIO_DATA.length - 2)
      : OP_MS_RADIO_DATA;
  }, [mapPriority, selectedGeohashMS]);

  const quadLegendLabels = useMemo(() => {
    if (msConfig) {
      const labelMap = {};
      const section = msConfig[0]?.content.find((x) => x.type === "quad");
      if (section && section.values) {
        section.values.forEach(x => {
          labelMap[x.colorText] = x.text;
        })
      }
      return labelMap;
    }
    return {};
  }, [msConfig]);

  // output: "adminLevel1:Johor,adminLevel2:Johor Bahru,adminLevel3:Jelutong"
  const admString = useMemo(() => {
    const { adminLevel1, adminLevel2, adminLevel3 } = appliedFilters;
    Object.entries({ adminLevel1, adminLevel2, adminLevel3 })
      .filter(([_, value]) => value)
      .map(([key, value]) => `${adminLevelToAdm[key]}:${value}`)
      .join(',')
  }, [appliedFilters]);

  // functions

  const adjustLvlBasedOnAdmLevel = (currentLvl, currentLvlArr = []) => {
    // enable/disable ADMs based on Administrative level selected
    let lvl = currentLvl;
    if (level === "adminLevel2") {
      if (currentLvl === "ADM1" || !currentLvl) lvl = "ADM2";
      disableADMToggles(["ADM1"], currentLvlArr);
    } else if (level === "adminLevel3") {
      if (currentLvl !== "ADM3") lvl = "ADM3";
      disableADMToggles(["ADM1", "ADM2"], currentLvlArr);
    } else if (level === "geoHashLevel") {
      lvl = null;
      disableADMToggles(["ADM1", "ADM2", "ADM3"], currentLvlArr);
    } else {
      if (!currentLvl && currentLvlArr.length) lvl = currentLvlArr[0].value;
      disableADMToggles([],  allBorderLvls.length ? allBorderLvls:currentLvlArr);
    }

    if (lvl !== borderLvlValue) setBorderLvlValue(lvl);
    fetchCTFilters({ lvl });
    fetchAggTypeFilters({ lvl });
  };

  const disableADMToggles = (adms = [], currentLvlArr) => {
    const admArr = [...currentLvlArr];
    for (const lvl of admArr) {
      lvl.disabled = adms.includes(lvl.value);
    }
    setBorderLvls(admArr);
  }

  // common func. to fetch all distict
  const fetchAllDistictFilters = async (table, column, query, cb) => {
    try {
      if (countryCode && year && quarter) {
        const { data } = await getAllDistinctWithCount(
          {
            countryCode,
            year,
            quarter,
            column,
            table,
          },
          query ? { ...query, hideLoader: true } : { hideLoader: true }
        );
        if (data) {
          const filtersList = data.map((x) => ({
            filterName: x._id,
            label: x._id,
            isChecked: x.count > 0,
            disabled: x.count <= 0,
            count: x.count
          }));
          if (cb) cb(filtersList);
        }
      }
    } catch (err) {
      console.log("Error while fetching connection types: ", err);
    }
  }

  // common func. to fetch values for radio filters.
  const fetchRadioFilters = async (table, column, query, cb) => {
    try {
      if (countryCode && year && quarter) {
        const { data } = await getFilters(
          {
            countryCode,
            year,
            quarter,
            column,
            table,
          },
          query ? { ...query, hideLoader: true } : { hideLoader: true }
        );
        if (data) {
          const options = data.map((x) => ({
            label: x._id,
            value: x._id,
          }));
          if (cb) cb(options);
        }
      }
    } catch (err) {
      console.log("Error while fetching connection types: ", err);
    }
  };

  // fetch market share type values (values for rsrp, download, upload etc)
  const fetchMsLvlFilters = async ({ radioVal = radioValue, ct, lvl, op, actions = actionFilters, quads = quadFilters, ranks = msRanks, ms = [range[0], range[1]] }) => {
    const addRanks = shouldAddToQuery(ranks);
    const addActions = shouldAddToQuery(actions);
    const checkedQuads = quads.filter(x => x.isChecked).map(x => x.filterName);
    try {
      fetchAllDistictFilters("operator_market_share", radioVal, {
        params: {
          filtered: `lvl:${lvl || borderLvlValue},ct:${ct || connectionValue}${panelFilters?.aggType ? `,agg_type:${panelFilters.aggType}` : ''},op:${op || selectedOperatorMS}${level ? `,${admString}` : ''}`,
          inFilters: `quad:${checkedQuads.join('|')}${addRanks ? `,ms_rank:${addRanks.join('|')}` : ''}${addActions ? `,action:${addActions.join('|')}` : ''}`,
          ...((ms[0] > 0 || ms[1] < 100) && { logicFilters: `ms:${ms[0] > 0 ? ms[0] + "$gte" : ""}${ms[0] > 0 && ms[1] < 100 ? "|" : ""}${ms[1] < 100 ? ms[1] + "$lte" : ""}` })
        },
      }, (data) => {
        const sortedFilters = sortByReference(data, radioVal == "action" ? DATA_ORDERS.NET_ACTION : radioVal == "quad" ? DATA_ORDERS.QUAD : DATA_ORDERS.SIG_LVL, "filterName");
        setConditionFilters(sortedFilters);
        setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
      });
    } catch (err) {
      console.log("Error while fetching ms type options: ", err);
    }
  }

  // fetch quad counts
  const fetchQuadCounts = async ({ lvl, op, ct, ranks = msRanks, actions = [], quads = [], ms = [range[0], range[1]] }) => {
    try {
      const addRanks = shouldAddToQuery(ranks);
      const addActions = shouldAddToQuery(actions);
      const { data = [] } = await getQuadCounts(
        {
          country: countryCode,
          quarter: `${quarter} ${year}`,
          ...(appliedFilters?.adminLevel1 && { adminLevel1: appliedFilters?.adminLevel1 }),
          ...(appliedFilters?.adminLevel2 && { adminLevel2: appliedFilters?.adminLevel2 }),
          ...(appliedFilters?.adminLevel3 && { adminLevel3: appliedFilters?.adminLevel3 }),
          admLvl: lvl || borderLvlValue,
          op: op || selectedOperatorMS,
          ct: ct || connectionValue,
          msRangeFrom: ms[0],
          msRangeTo: ms[1],
          ...(addRanks && { rank: convertArrayToStringWithDelimeter(addRanks, "_") }),
          ...(addActions && { action: convertArrayToStringWithDelimeter(addActions, "_") }),
        }
      );
      const quadArr = quads.map(x => {
        const countObj = data.find(y => y.quad === x.filterName);
        return { ...x, label: countObj && !x.disabled ? `${x.label} (${countObj.admCount} / ${countObj.geohashCount})` : x.label }
      });
      setQuadCount(data);
      setQuadFilters(quadArr);
    } catch (err) {
      console.log("Error while fetching quad counts: ", err);
    }
  }

  // fetch quad values
  const fetchQuadFilters = ({ ct, lvl, op, ranks = msRanks, actions = [], ms = [range[0], range[1]] }) => {
    const addRanks = shouldAddToQuery(ranks);
    const addActions = shouldAddToQuery(actions);
    fetchAllDistictFilters("operator_market_share", "quad", {
      params: {
        filtered: `lvl:${lvl || borderLvlValue},op:${op || selectedOperatorMS},ct:${ct || connectionValue}${level ? `,${admString}` : ''}`,
        inFilters: `${addRanks ? `ms_rank:${addRanks.join('|')}` : ''}${addRanks && addActions ? ',' : ''}${addActions ? `action:${addActions.join('|')}` : ''}`,
        ...((ms[0] > 0 || ms[1] < 100) && { logicFilters: `ms:${ms[0] > 0 ? ms[0] + "$gte" : ""}${ms[0] > 0 && ms[1] < 100 ? "|" : ""}${ms[1] < 100 ? ms[1] + "$lte" : ""}` })
      },
    }, (data) => {
      const quadArr = sortByReference(data, DATA_ORDERS.QUAD, "filterName");
      const renamed = renameLabels(quadArr, quadLegendLabels);
      setQuadFilters(renamed);
      fetchMsLvlFilters({ ct, lvl, op, ranks, actions, quads: renamed, ms });
      fetchQuadCounts({ ct, lvl, op, ranks, actions, quads: renamed, ms })
    });
  }

  // fetch action values
  const fetchActionFilters = ({ ct, lvl, op, ranks = msRanks, ms = [range[0], range[1]] }) => {
    const addRanks = shouldAddToQuery(ranks);
    fetchAllDistictFilters("operator_market_share", "action", {
      params: {
        filtered: `lvl:${lvl || borderLvlValue},op:${op || selectedOperatorMS},ct:${ct || connectionValue}${level ? `,${admString}` : ''}`,
        ...(addRanks && { inFilters: `ms_rank:${addRanks.join('|')}` }),
        ...((ms[0] > 0 || ms[1] < 100) && { logicFilters: `ms:${ms[0] > 0 ? ms[0] + "$gte" : ""}${ms[0] > 0 && ms[1] < 100 ? "|" : ""}${ms[1] < 100 ? ms[1] + "$lte" : ""}` })
      },
    }, (data) => {
      const actionArr = sortByReference(data, DATA_ORDERS.NET_ACTION, "filterName").map(x => ({ ...x, isChecked: false }));
      setActionFilters(actionArr);
      fetchQuadFilters({ ct, lvl, op, actions: actionArr, ranks, ms });
    });
  }

  // fetch ms rank values
  const fetchRankFilters = ({ lvl, op, ct, ms = [range[0], range[1]] }) => {
    fetchAllDistictFilters("operator_market_share", "ms_rank", {
      params: {
        filtered: `lvl:${lvl || borderLvlValue},op:${op || selectedOperatorMS},ct:${ct || connectionValue}${level ? `,${admString}` : ''}`,
        ...((ms[0] > 0 || ms[1] < 100) && { logicFilters: `ms:${ms[0] > 0 ? ms[0] + "$gte" : ""}${ms[0] > 0 && ms[1] < 100 ? "|" : ""}${ms[1] < 100 ? ms[1] + "$lte" : ""}` })
      },
    }, (data) => {
      data.sort((a, b) => Number(a.filterName) - Number(b.filterName));
      const ranks = data.map(x => ({ ...x, label: x.filterName, isChecked: false }));
      setMsRanks(ranks);
      fetchActionFilters({ lvl, op, ct, ranks, ms });
    });
  }

  // fetch connection_types values
  const fetchCTFilters = ({ lvl, op }) => {
    fetchRadioFilters("operator_market_share", "ct", {
      params: {
        filters: `lvl:${lvl || borderLvlValue},op:${op || selectedOperatorMS}`,
      },
    }, (data) => {
      const ctArr = sortByReference(data, DATA_ORDERS.CT, "label");
      if (!OP_MS_TRANSMISSION_DATA.some(item => item.value === radioValue)) {
        fetchAggTypeFilters({lvl, ct: ctArr[0]?.value,  op})
      }
      setConnectionValue(ctArr[0]?.value);
      setMsConfig(msConfigType?.[`${ctArr[0]?.value}`])
      setConnectionTypes(ctArr);
      fetchRankFilters({ ct: ctArr[0]?.value, lvl, op });  
    });
  }

  // fetch agg_types values
  const fetchAggTypeFilters = ({ lvl, ct, op}) => {
    if (opMSAggrPerm?.is_enable !== true) return; // Exit early if the user does not permitted
    
    fetchRadioFilters("operator_market_share", "agg_type", {
      params: {
        filters: `lvl:${lvl || borderLvlValue},op:${op || selectedOperatorMS},ct:${ct || connectionValue}`,
      },
    }, (data) => {
      const aggTypeArr = sortByReference(data, DATA_ORDERS.AGGR_TYPE, "label");
      setAggValue(aggTypeArr[0]?.value);
      setAggTypes(aggTypeArr);
      setPanelFilters((prevFilters) => ({
        ...prevFilters,
        lvl,
        aggType: aggTypeArr[0]?.value,
      }));
    });
  }

  // common func. handle toggle filters chnage
  const onChangeToggleFilter = (options = [], { filterName }, cb) => {
    const updatedFilters = [...options];
    const idx = updatedFilters.findIndex(x => x.filterName === filterName);
    updatedFilters[idx] = { ...updatedFilters[idx], isChecked: !updatedFilters[idx].isChecked }
    if (cb) cb(updatedFilters);
  };

  const handleShowResetModal = (cb) => {
    if (isResetModal) setResetPopup({
      open: true,
      cb
    });
    else cb();
  }

  const getMinMaxMSOp = async (radioValue) => {
        try {
          const { countryCode, year, quarter } = appliedFilters;
      
          const { data } = await fetchMinMaxMSOp({
            country: countryCode,
            quarter: `${quarter} ${year}`,
            ct: connectionValue,
            op: selectedOperatorMS,
            msType: radioValue,
            lvl: borderLvlValue
          });
          if (data) {           
            setMinMaxMSOp((prevState) => ({
              ...prevState, 
              [radioValue]: data, 
            }));
          }
        } catch (e) {
          console.log("Fetch gradient min max failed: ", e);
        }
      };

//   const CustomText = styled.div`
//   display: flex;  // Use flexbox to align children inline
//   align-items: center;  // Align items vertically in the center
//   color: ${props => props.color || 'black'};  // Default color is black
//   font-size: ${props => props.size || '16px'};  // Default font size is 16px
//   text-align: center;  // Center the text by default
//   margin: 2px 0;  // Add some vertical margin for separation
// `;

// const Icon = styled.img`
//   width: 12px;  // Set a fixed width for the icon
//   height: 12px;  // Set a fixed height for the icon
//   margin-right: 2px;  // Space between the icon and the text
// `;

// const Text = styled.span`  // Wrap text in a span for better control
//   display: inline;  // Inline display to keep text aligned with the icon
// `;



  return (
    <>
      {isVisible && !topMSChecked ? (
        <>
          <StyledContainer>
            <CommonTypeTabsGroup
              data={borderLvls}
              value={borderLvlValue}
              handleClick={(val) => {
                const cb = () => {
                  setBorderLvlValue(val);
                  fetchCTFilters({ lvl: val });
                  fetchAggTypeFilters({ lvl: val });
                };
                handleShowResetModal(cb);
              }}
              icon={false}
            />
          </StyledContainer>
          <DisabledInfoMessage
        isVisible={hasDisabledTab}
        message="Button disabled due to geographical filters."
        iconSrc="/assets/information.png"
        opacity={0.8}
      />
          <SitesDetailsTile
            title="Operator Sites"
            selectedOperator={selectedOperatorMS}
            onchangeOperator={(val) => {
              const cb = () => {
                setSelectedOperatorMS(val);
                fetchCTFilters({ op: val });
              };
              handleShowResetModal(cb);
            }}
            permitedOperatorData={operatorData}
          />
          <StyledContainer>
            <CommonTypeTabsGroup
              data={connectionTypes}
              value={connectionValue}
              handleClick={(val) => {
                const cb = () => {
                  setConnectionValue(val);
                  setMsConfig(msConfigType?.[`${val}`])
                  fetchRankFilters({ ct: val });
                };
                handleShowResetModal(cb);
              }}
              icon={true}
            />
          </StyledContainer>
          <StyledGridContainer container item>
            <CommonRangeSelectSlider
              title="Market Share"
              value={range}
              onChange={(min, max) => {
                const cb = () => {
                  setRange([min, max]);
                  fetchRankFilters({ ms: [min, max] });
                };
                handleShowResetModal(cb);
              }}
              min={0}
              max={100}
              steps={10}
            />
            <HorizontalSeparator />
            <CommonHorizMultiSelect
              title={"Market Share Rank"}
              data={msRanks}
              handleChange={(val) => {
                const cb = () => {
                  onChangeToggleFilter(msRanks, val, (updatedArr) => {
                    setMsRanks(updatedArr);
                    fetchActionFilters({ ranks: updatedArr });
                  });
                };
                handleShowResetModal(cb);
              }}
            />
            <HorizontalSeparator />
            <CommonHorizMultiSelect
              title={"Network Action"}
              data={actionFilters}
              multiLineLabel={true}
              handleChange={(val) => {
                const cb = () => {
                  onChangeToggleFilter(actionFilters, val, (updatedArr) => {
                    setActionFilters(updatedArr);
                    fetchQuadFilters({ actions: updatedArr });
                  });
                };
                handleShowResetModal(cb);
              }}
            />
            <HorizontalSeparator />

            <StyledGridTitle item xs={12} sm={12} md={12}>
              Quadrant
            </StyledGridTitle>
            <CommonToggleWrapper
              filtersArray={quadFilters.map(filter => ({
                ...filter,
                label: filter.label.includes("Not Enough Data") ? (
                  <span style={{ textAlign: 'right', display: 'block' }}>{filter.label}</span>
                ) : (
                  <span style={{ marginLeft: '7px' }}>{filter.label}</span>
                )
              }))}
              tooltip={"Count of Admin Levels / Count of Geohashes"}
              onChangeStatusFilter={(val) => {
                const cb = () => {
                  onChangeToggleFilter(quadFilters, val, (updatedArr) => {
                    setQuadFilters(updatedArr);
                    fetchMsLvlFilters({ quads: updatedArr });
                  });
                };
                handleShowResetModal(cb);
              }}
            />
          </StyledGridContainer>
          
          {!isModalOpen && (
            <StyledGridContainer container item>
              {aggValue != null &&
              <CommonTypeTabsGroup
              title="Aggregation Type"
              data={aggTypes}
              value={panelFilters?.aggType}
              handleChange={(val) => {               
                setPanelFilters((prevFilters) => ({
                  ...prevFilters,
                  aggType: val,
                }));

                const newRadioValue = OP_MS_RADIO_DATA[0]?.value;
                if (OP_MS_TRANSMISSION_DATA.some(item => item.value === radioValue)) {
                  setRadioValue(newRadioValue);
                  fetchMsLvlFilters({ radioVal: newRadioValue });
                }else{
                  if (!skipLevelFilters.includes(radioValue)) {
                    fetchMsLvlFilters({ radioVal: radioValue });
                  }
                  else {
                    setConditionFilters([]);
                    setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                  }
                }                 
              }}
              icon={false}
            />
            }
              <CommonRadioSelectGroup
                items={MS_LEVELS}
                value={radioValue}
                onChange={(val) => {
                  if (
                    OP_MS_TRANSMISSION_DATA.some((item) => item.value === radioValue) &&
                    !panelFilters.aggType
                  ) {
                    setPanelFilters((prevFilters) => ({
                      ...prevFilters,
                      aggType: aggValue,
                    }));
                  }
                  setRadioValue(val);
                  if (!skipLevelFilters.includes(val)) fetchMsLvlFilters({ radioVal: val });
                  else {
                    setConditionFilters([]);
                    setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                  }
                }}
              />
              <HorizontalSeparator />
              <CommonRadioSelectGroup
                items={OP_MS_TRANSMISSION_DATA}
                value={radioValue}
                onChange={ async (val) => {
                  await getMinMaxMSOp(val);
                  setRadioValue(val);
                  setPanelFilters((prevFilters) => ({
                    ...prevFilters,
                    aggType: null,
                  }));   
                  setConditionFilters([]);
                  setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                }}
              />
              {!skipLevelFilters.includes(radioValue) && !OP_MS_TRANSMISSION_DATA.some(item => item.value === radioValue) &&// hide lvl filters for ms and ms_rank
                <>
                  <HorizontalSeparator />
                  <CommonToggleWrapper
                    filtersArray={conditionFilters}
                    onChangeStatusFilter={(val) => {
                      onChangeToggleFilter(conditionFilters, val, (updatedArr) => {
                        setConditionFilters(updatedArr);
                        setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                      });
                    }}
                  />
                </>
              }
            </StyledGridContainer>
          )}
        </>
      ) : (
        <></>
      )}
      {!topMSChecked && isModalOpen &&
        <OperatorMSModal
          isOpen={isModalOpen}
          setIsOpen={handleModalClose}
          borderLvlValue={borderLvlValue}
          connectionValue={connectionValue}
          selectedOperatorSupply={selectedOperatorMS}
          range={range}
          setSavedRows={setSelectedMS}
          savedRows={selectedMS}
          setSavedGeoRows={setSelectedGeohashMS}
          savedGeoRows={selectedGeohashMS}
          action={actionFilters}
          quad={quadFilters}
          msRanks={msRanks}
          apiTrigger={filtersTrigger}
          setVisualizePriority={setMapPriority}
          msConfig={msConfig}
          geoHashTableColumns={geoHashTableColumns}
          filterColumnsByLevel={filterColumnsByLevel}
          filteredColumns={filteredColumns}
          geohashfilteredColumns={geohashfilteredColumns}
          setGeohashfilteredColumns={setGeohashfilteredColumns}
          msSort={msSort}
          setMsSort={setMsSort}
          geoMsSort={geoMsSort}
          setGeoMsSort={setGeoMsSort}
          quadCount={quadCount}
          permittedOperators={operatorData}
        />}
      {eyeIcon && !topMSChecked && !isModalOpen && borderLvlValue && (
        <>
          <OperatorMSMapElements
            borderLvl={borderLvlValue}
            connectionType={connectionValue}
            aggType={panelFilters?.aggType}
            operator={selectedOperatorMS}
            msRangeFrom={range[0]}
            msRangeTo={range[1]}
            action={actionFilters}
            quad={quadFilters}
            msType={radioValue}
            msLevel={conditionFilters}
            selectedMS={selectedMS}
            msRanks={msRanks}
            apiTrigger={filtersTrigger}
            mapPriority={mapPriority}
            selectedGeohashes={selectedGeohashMS}
            eyeIcon={eyeIcon}
            radioValue={radioValue}
            borderLvls={borderLvls}
            permittedOperators={operatorData}
            minMaxMSOp = {minMaxMSOp}
          />
        </>
      )}
      {resetPopup.open &&
        <WarningModal
          open={resetPopup.open}
          acceptAction={() => {
            if (resetPopup.cb) resetPopup.cb();
            setResetPopup({ open: false, cb: () => { } });
          }}
          cancelAction={() => {
            setResetPopup({ open: false, cb: () => { } });
          }}
        />}
    </>
  );
};

export default OperatorMSPanel;
